declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
    googleAnalyticsConsent?: boolean
  }
}

const CONSENT_DEFAULTS = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  wait_for_update: 500,
} as const

/**
 * Starts GA4 only after analytics consent has been granted.
 * Consent Mode is still updated if the visitor later changes that decision.
 */
export function configureGoogleAnalytics(measurementId: string, analyticsConsent: boolean) {
  if (!measurementId || typeof window === 'undefined') return

  window.googleAnalyticsConsent = analyticsConsent
  if (!analyticsConsent && typeof window.gtag !== 'function') return

  if (typeof window.gtag !== 'function') {
    window.dataLayer = window.dataLayer || []
    window.gtag = function () {
      window.dataLayer?.push(arguments)
    }
    window.gtag('consent', 'default', CONSENT_DEFAULTS)
    window.gtag('js', new Date())
    window.gtag('config', measurementId, { send_page_view: false })

    const script = document.createElement('script')
    script.id = 'google-analytics-script'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    script.onerror = () => console.error('Google Analytics could not be loaded.')
    document.head.appendChild(script)
  }

  const signal = analyticsConsent ? 'granted' : 'denied'
  window.gtag('consent', 'update', { analytics_storage: signal })
}

/**
 * Reports an event to Google Analytics. Does nothing when gtag is not on the
 * page: analytics not set up, declined, or stripped by a content blocker.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  if (window.googleAnalyticsConsent !== true) return

  window.gtag('event', name, params)
}

export function trackPageView(path: string) {
  if (typeof window === 'undefined') return

  trackEvent('page_view', {
    page_location: new URL(path, window.location.origin).toString(),
    page_title: document.title,
  })
}
